import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import './Login.css';

const Login = () => {
  const { user, login } = useAuth();
  const { tenantConfig } = useTenant();
  const navigate = useNavigate();
  const params = useParams();

  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData(e.target);
    const credentials = {
      username: data.get('username')?.trim(),
      password: data.get('password'),
    };

    if (!credentials.username || !credentials.password) {
      setError('Please enter your username and password.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await login(credentials);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Login failed. Please check your credentials and try again.';
      setError(msg);
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('shipEngineAPI');
    } finally {
      setLoading(false);
    }
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      const prefix = params.tenantPrefix;
      navigate(prefix ? `/${prefix}/dashboard` : '/dashboard');
    }
  }, [user, navigate, params.tenantPrefix]);

  const accentColor = tenantConfig.primaryColor || '#3b82f6';

  return (
    <div className="login-root">
      {/* Left panel — branding */}
      <div className="login-panel login-panel--brand" style={{ '--login-accent': accentColor }}>
        <div className="login-brand-bg-grid" aria-hidden="true" />
        <div className="login-brand-content">
          <div className="login-brand-logo-wrap">
            <img
              src={tenantConfig.loginLogo || '/bannernew.png'}
              alt={tenantConfig.tenantName || 'Portal'}
              className="login-brand-logo"
              onError={(e) => { e.target.src = '/bannernew.png'; }}
            />
          </div>
          <div>
            <h2 className="login-brand-headline">
              Manage your freight,<br />effortlessly.
              <span>Real-time visibility into your inventory, shipments &amp; logistics.</span>
            </h2>
          </div>
          <div className="login-brand-divider" />
          <div className="login-brand-features">
            {[
              'Real-time inventory tracking',
              'Shipping & Bill of Lading management',
              'Order approvals & status updates',
              'Warehouse receipt tracking',
              'Invoice management',
            ].map((f) => (
              <div key={f} className="login-brand-feature">
                <span className="login-brand-feature-icon" aria-hidden="true">✓</span>
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="login-brand-footer">
          Powered by FMS
        </div>
      </div>

      {/* Right panel — form */}
      <div className="login-panel login-panel--form">
        <div className="login-form-card">
          {/* Mobile logo */}
          <div className="login-mobile-logo">
            <img
              src={tenantConfig.loginLogo || '/bannernew.png'}
              alt={tenantConfig.tenantName || 'Portal'}
              onError={(e) => { e.target.src = '/bannernew.png'; }}
            />
          </div>

          <div className="login-form-header">
            <h1>Welcome back</h1>
            <p>Sign in to your customer portal</p>
          </div>

          <form onSubmit={handleSubmit} noValidate autoComplete="on">
            <div className="login-field">
              <label htmlFor="login-username" className="login-label">Username</label>
              <div className="login-input-wrap">
                <span className="login-input-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                <input
                  id="login-username"
                  type="text"
                  name="username"
                  className="login-input"
                  placeholder="Enter your username"
                  required
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="login-password" className="login-label">Password</label>
              <div className="login-input-wrap">
                <span className="login-input-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  id="login-password"
                  type={showPw ? 'text' : 'password'}
                  name="password"
                  className="login-input"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="login-pw-toggle"
                  onClick={() => setShowPw(!showPw)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPw ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="login-error" role="alert">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="login-submit"
              disabled={loading}
              style={{ '--login-accent': accentColor }}
            >
              {loading ? (
                <>
                  <span className="login-spinner" />
                  Signing in…
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="login-help-text">
            Need access? Contact your account administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
